/**
 * BookingDetail.jsx
 * 
 * Customer booking detail / ticket page.
 * Shows full booking information including:
 * - Confirmation code
 * - Service details
 * - Date/time
 * - Party size
 * - Pricing breakdown
 * - Operator contact info
 * - Meeting location
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import {
  ChevronLeft,
  Calendar,
  Clock,
  Users,
  MapPin,
  Phone,
  Mail,
  Building,
  Ticket,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  Share2,
  Star,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BookingDetail = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useCustomerAuth();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/?signin=true');
      return;
    }
    
    if (isAuthenticated && bookingId) {
      fetchBooking();
    }
  }, [authLoading, isAuthenticated, bookingId]);

  const fetchBooking = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/customer/bookings/${bookingId}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setBooking(data);
      } else if (response.status === 404) {
        setError('Booking not found');
      } else {
        setError('Failed to load booking');
      }
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError('Failed to load booking');
    } finally {
      setLoading(false);
    }
  };

  const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Pending' },
    confirmed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Confirmed' },
    completed: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle, label: 'Completed' },
    cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Cancelled' },
    no_show: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'No Show' }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <Card>
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-6 w-36" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{error}</h2>
          <Button variant="outline" onClick={() => navigate('/my-trips')}>
            Back to My Trips
          </Button>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  const status = statusConfig[booking.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/my-trips')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">Booking Details</h1>
          </div>
          <Button variant="ghost" size="sm" className="gap-2">
            <Share2 className="w-4 h-4" />
            Share
          </Button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Ticket Card */}
        <Card className="overflow-hidden">
          {/* Service Image */}
          {booking.service_image && (
            <div className="h-48 overflow-hidden">
              <img 
                src={booking.service_image} 
                alt={booking.service_name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <CardContent className="p-6">
            {/* Confirmation Code */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-500">Confirmation</span>
              </div>
              <span className="font-mono text-lg font-bold text-gray-900">
                {booking.confirmation_code}
              </span>
            </div>

            {/* Status Badge */}
            <div className="mb-4">
              <Badge className={`${status.color} gap-1`}>
                <StatusIcon className="w-3 h-3" />
                {status.label}
              </Badge>
            </div>

            {/* Service Name */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {booking.service_name}
            </h2>

            {/* Operator */}
            {booking.organization && (
              <p className="text-gray-600 mb-4">
                by {booking.organization.name}
              </p>
            )}

            <div className="border-t border-gray-200 pt-4 space-y-3">
              {/* Date */}
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">
                    {formatDate(booking.booking_date)}
                  </p>
                </div>
              </div>

              {/* Time */}
              {booking.start_time && (
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {formatTime(booking.start_time)}
                      {booking.end_time && ` - ${formatTime(booking.end_time)}`}
                    </p>
                    {booking.duration_minutes && (
                      <p className="text-sm text-gray-500">
                        {booking.duration_minutes} minutes
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Party Size */}
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-gray-400" />
                <p className="font-medium text-gray-900">
                  {booking.party_size} {booking.party_size === 1 ? 'Guest' : 'Guests'}
                </p>
              </div>

              {/* Meeting Location */}
              {booking.meeting_location && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Meeting Point</p>
                    <p className="text-gray-600">{booking.meeting_location}</p>
                    {booking.location_details && (
                      <p className="text-sm text-gray-500">{booking.location_details}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pricing Card */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Price Details</h3>
            
            <div className="space-y-2">
              {booking.base_price && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Base price</span>
                  <span className="text-gray-900">${booking.base_price.toFixed(2)}</span>
                </div>
              )}
              
              {booking.add_ons_total > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Add-ons</span>
                  <span className="text-gray-900">${booking.add_ons_total.toFixed(2)}</span>
                </div>
              )}
              
              {booking.sales_tax > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Taxes</span>
                  <span className="text-gray-900">${booking.sales_tax.toFixed(2)}</span>
                </div>
              )}
              
              {booking.service_fee > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Service fee</span>
                  <span className="text-gray-900">${booking.service_fee.toFixed(2)}</span>
                </div>
              )}
              
              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="flex justify-between font-semibold">
                  <span className="text-gray-900">Total paid</span>
                  <span className="text-gray-900">
                    {booking.currency} ${booking.total_amount?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Operator Contact Card */}
        {booking.organization && (
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Contact Operator</h3>
              
              <div className="flex items-center gap-4">
                {booking.organization.logo_url ? (
                  <img 
                    src={booking.organization.logo_url}
                    alt={booking.organization.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                    <Building className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-900">{booking.organization.name}</p>
                  {booking.city && (
                    <p className="text-sm text-gray-500">{booking.city}</p>
                  )}
                </div>
              </div>
              
              <div className="mt-4 space-y-2">
                {booking.organization.phone && (
                  <a 
                    href={`tel:${booking.organization.phone}`}
                    className="flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <Phone className="w-4 h-4" />
                    {booking.organization.phone}
                  </a>
                )}
                {booking.organization.email && (
                  <a 
                    href={`mailto:${booking.organization.email}`}
                    className="flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <Mail className="w-4 h-4" />
                    {booking.organization.email}
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Review Section */}
        {booking.has_reviewed && booking.review && (
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Your Review</h3>
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star}
                    className={`w-5 h-5 ${
                      star <= booking.review.rating
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              {booking.review.title && (
                <p className="font-medium text-gray-900">{booking.review.title}</p>
              )}
              {booking.review.comment && (
                <p className="text-gray-600 mt-1">{booking.review.comment}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Back Button */}
        <div className="pt-4">
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate('/my-trips')}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to My Trips
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingDetail;
